import React, { createContext, useContext, useState, useEffect } from 'react'
import { useZoomContext } from './ZoomContext'
import { Participant } from '@zoom/videosdk'
import { useUI } from './UIContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { useCourseStudyPlan } from '../hooks/useCourseStudyPlan'
import { useSessionParams } from '../hooks/useSessionParams'
import { useUserCourses } from '../pages/meeting-view/hooks/useCourses'
import { NotificationWithCross } from '../components/NotificationWithCross'

// Session storage key for last seen step
export const LAST_SEEN_STEP_KEY = 'egg_video_sdk_last_seen_step'

interface ContentSharingContextType {
    isContentShared: boolean
    setIsContentShared: (isContentShared: boolean) => void
    isContentSharer: boolean
    currentSharedStep: string | null
    setCurrentSharedStep: (stepId: string) => void
    shareContent: () => void
    stopSharingContent: () => void
    navigateToStep: (stepId: string) => void
    shareVideo: (videoUrl: string) => void
    shareImage: (src: string, alt?: string) => void
    isFeatureEnabled: boolean
    currentStepId: string | undefined
    setCurrentStepId: (stepId: string) => void
}

const ContentSharingContext = createContext<ContentSharingContextType | undefined>(undefined)

export const ContentSharingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation('notifications')
    const { commandChannel, zoomClient } = useZoomContext()
    const { showToast, openPip } = useUI()
    const { _classId: courseId, room } = useSessionParams()
    const { currentCourse } = useUserCourses()
    const { firstStepId, findStepById } = useCourseStudyPlan(courseId)
    const enableSharedContent = !!currentCourse?.courseFlags.enableSharedContent

    const [isContentShared, setIsContentShared] = useState(false)
    const [sharerUserId, setSharerUserId] = useState<number | null>(null)
    const [currentSharedStep, setCurrentSharedStep] = useState<string | null>(null)
    const [countdownTimers, setCountdownTimers] = useState<Record<string, NodeJS.Timeout>>({})
    const [currentStepId, setCurrentStepId] = useState<string | undefined>(undefined)

    const userId = zoomClient.getSessionInfo().userId

    const isContentSharer = userId === sharerUserId

    // Helper function to save the current step to sessionStorage
    const saveLastSeenStep = (stepId: string) => {
        try {
            sessionStorage.setItem(LAST_SEEN_STEP_KEY, stepId)
        } catch (error) {
            console.error('Error saving last seen step to sessionStorage:', error)
        }
    }

    // Helper function to get the last seen step from sessionStorage
    const getLastSeenStep = (): string | null => {
        try {
            return sessionStorage.getItem(LAST_SEEN_STEP_KEY)
        } catch (error) {
            console.error('Error getting last seen step from sessionStorage:', error)
            return null
        }
    }

    // Helper function to get user's display name
    const getShareUserName = (uid: number | string) => {
        try {
            // Convert to numbers for comparison to handle type mismatches
            const uidNumber = Number(uid)

            if (isNaN(uidNumber)) {
                return 'Someone'
            }

            const allUsers = zoomClient.getAllUser()

            if (allUsers.length === 0) {
                return 'Someone'
            }

            // Try multiple approaches to find the user
            let user = allUsers.find(u => u.userId === uid) // Exact match

            if (!user) {
                user = allUsers.find(u => Number(u.userId) === uidNumber) // Number conversion
            }

            if (!user) {
                user = allUsers.find(u => String(u.userId) === String(uid)) // String conversion
            }

            if (user) {
                return user.displayName
            } else {
                // As a fallback, try to get the user directly if possible
                try {
                    const directUser = zoomClient.getUser(uidNumber)
                    if (directUser) {
                        return directUser.displayName
                    }
                } catch (error) {
                    // Fallback to default
                }
                return 'Someone'
            }
        } catch (error) {
            return 'Someone'
        }
    }

    // Show notification when content sharing state changes
    const showContentSharingNotification = (action: 'start' | 'stop', sharerUserId: number | null) => {
        if (action === 'start' && sharerUserId) {
            const sharerName = getShareUserName(sharerUserId)
            const icon = <FontAwesomeIcon icon={['far', 'folder-open']} style={{ color: '#48A0F8' }} />

            if (sharerUserId === userId) {
                // If I'm the sharer
                showToast(t('commandChannel.youAreSharing'), {
                    id: `content-sharing-${sharerUserId}`,
                    duration: Infinity,
                    icon,
                })
            } else {
                // If someone else is sharing
                showToast(t('commandChannel.contentSharingStarted', { name: sharerName }), {
                    id: `content-sharing-${sharerUserId}`,
                    duration: Infinity,
                    icon,
                })
            }
        } else if (action === 'stop' && sharerUserId) {
            showToast.dismiss(`content-sharing-${sharerUserId}`)
        }
    }

    // Listen for command channel messages
    useEffect(() => {
        if (!commandChannel || !enableSharedContent) return

        const handleCommandMessage = (payload: any) => {
            try {
                // Check if the message is in payload.message or payload.text
                const messageText = payload.message || payload.text

                if (!messageText) {
                    console.error('No message content found in payload:', payload)
                    return
                }

                const data = JSON.parse(messageText)

                if (data.type === 'content_sharing') {
                    if (data.action === 'start') {
                        // Update state for everyone
                        setIsContentShared(true)
                        setSharerUserId(payload.senderId)

                        // Ensure stepId is a string
                        let stepId = String(data.stepId)

                        // Verify that the step exists in the current session
                        const stepExists = !!findStepById(stepId)

                        // If the step doesn't exist, use the first step instead
                        if (!stepExists && firstStepId) {
                            stepId = firstStepId
                        }

                        setCurrentSharedStep(stepId)

                        // Save the step to sessionStorage for all participants
                        saveLastSeenStep(stepId)

                        // Show notification for everyone except the sharer
                        if (payload.senderId !== userId) {
                            // Use the helper function instead of direct showToast
                            showContentSharingNotification('start', payload.senderId)
                        }
                    } else if (data.action === 'stop') {
                        // Only process stop commands from the current sharer
                        if (sharerUserId !== payload.senderId) {
                            return
                        }

                        // Dismiss notification before resetting state
                        if (sharerUserId && sharerUserId !== userId) {
                            showToast.dismiss(`content-sharing-${sharerUserId}`)
                        }

                        setIsContentShared(false)
                        setSharerUserId(null)
                        setCurrentSharedStep(null)

                        // Note: We don't clear the last seen step from sessionStorage
                        // so it can be used when sharing starts again
                    } else if (data.action === 'navigate') {
                        // Only process navigate commands from the current sharer
                        if (sharerUserId !== payload.senderId) {
                            return
                        }

                        // Ensure stepId is a string
                        let stepId = String(data.stepId)

                        // Verify that the step exists in the current session
                        const stepExists = !!findStepById(stepId)

                        // If the step doesn't exist, use the first step instead
                        if (!stepExists && firstStepId) {
                            stepId = firstStepId
                        }

                        setCurrentSharedStep(stepId)

                        // Save the step to sessionStorage for all participants
                        saveLastSeenStep(stepId)
                    } else if (data.action === 'status_request') {
                        // Only the content sharer should respond to status requests
                        if (isContentSharer && currentSharedStep !== null) {
                            commandChannel?.send(
                                JSON.stringify({
                                    type: 'content_sharing',
                                    action: 'start',
                                    stepId: currentSharedStep,
                                }),
                                payload.senderId
                            )
                        }
                    } else if (data.action === 'share_video') {
                        // Handle video sharing
                        const { videoUrl } = data

                        // If I'm the sender, don't show countdown (I'll see the video immediately)
                        if (payload.senderId === userId) {
                            return
                        }

                        // For other participants, show countdown notification
                        const sharerName = getShareUserName(payload.senderId)
                        let secondsLeft = 5

                        // Create a unique ID for this video sharing event
                        const videoSharingId = `video-sharing-${payload.senderId}-${Date.now()}`

                        // Show initial notification
                        showToast(
                            t('commandChannel.videoWillPlay', {
                                name: sharerName,
                                seconds: secondsLeft,
                            }),
                            {
                                id: videoSharingId,
                                duration: Infinity,
                                icon: <FontAwesomeIcon icon={['fas', 'play-circle']} style={{ color: '#48A0F8' }} />,
                            }
                        )

                        // Start countdown
                        const countdownInterval = setInterval(() => {
                            secondsLeft -= 1

                            if (secondsLeft > 0) {
                                // Update countdown notification
                                showToast(
                                    t('commandChannel.videoWillPlay', {
                                        name: sharerName,
                                        seconds: secondsLeft,
                                    }),
                                    {
                                        id: videoSharingId,
                                        duration: Infinity,
                                        icon: (
                                            <FontAwesomeIcon
                                                icon={['fas', 'play-circle']}
                                                style={{ color: '#48A0F8' }}
                                            />
                                        ),
                                    }
                                )
                            } else {
                                // Clear interval and dismiss notification
                                clearInterval(countdownInterval)
                                showToast.dismiss(videoSharingId)

                                // Remove from tracked timers
                                setCountdownTimers(prev => {
                                    const newTimers = { ...prev }
                                    delete newTimers[videoSharingId]
                                    return newTimers
                                })

                                // Open the video in PiP
                                const embeddedUrl = getEmbeddedYouTubeUrl(videoUrl)
                                openPip({
                                    src: embeddedUrl,
                                    mute: false,
                                    onClose: () => {
                                        // Any cleanup needed when Pip is closed
                                    },
                                })
                            }
                        }, 1000)

                        // Track the timer so we can clear it if needed
                        setCountdownTimers(prev => ({
                            ...prev,
                            [videoSharingId]: countdownInterval,
                        }))
                    }
                }
            } catch (error) {
                console.error('Error parsing command message:', error, 'Payload:', payload)
            }
        }

        zoomClient.on('command-channel-message', handleCommandMessage)

        return () => {
            zoomClient.off('command-channel-message', handleCommandMessage)
        }
    }, [
        zoomClient,
        commandChannel,
        isContentSharer,
        currentSharedStep,
        userId,
        sharerUserId,
        showToast,
        t,
        openPip,
        enableSharedContent,
    ])

    // Request content sharing status when joining
    useEffect(() => {
        // Only run this once when the component mounts and if feature is enabled
        if (!enableSharedContent || room === 'general') return

        const requestContentStatus = async () => {
            // Wait a moment to ensure connection is established
            setTimeout(async () => {
                if (commandChannel) {
                    try {
                        // Broadcast a request for content sharing status
                        await commandChannel.send(
                            JSON.stringify({
                                type: 'content_sharing',
                                action: 'status_request',
                                timestamp: Date.now(), // Add timestamp to make each request unique
                            })
                        )
                    } catch (error) {
                        console.error('Error requesting content status:', error)
                    }
                }
            }, 2000)
        }

        requestContentStatus()
    }, [commandChannel, enableSharedContent, room])

    // Monitor user disconnections
    useEffect(() => {
        if (!enableSharedContent || room === 'general') return

        const handleUserRemoved = (removedUsers: Participant[]) => {
            // Check if the sharer was removed
            if (sharerUserId && removedUsers.some(user => Number(user.userId) === Number(sharerUserId))) {
                // Double-check that the user is actually gone
                setTimeout(() => {
                    const allUsers = zoomClient.getAllUser()
                    const sharerStillConnected = allUsers.some(user => Number(user.userId) === Number(sharerUserId))

                    if (!sharerStillConnected) {
                        // Show a notification to all users that the content sharing has ended
                        const sharerName = getShareUserName(sharerUserId)
                        const icon = <FontAwesomeIcon icon={['far', 'folder-open']} style={{ color: '#48A0F8' }} />

                        // First dismiss any existing notification
                        showToast.dismiss(`content-sharing-${sharerUserId}`)

                        // Then show a temporary notification about disconnection
                        showToast(t('commandChannel.sharerDisconnected', { name: sharerName }), {
                            id: `content-sharing-disconnect-${sharerUserId}`,
                            duration: 5000, // Show for 5 seconds
                            icon,
                        })

                        // Reset state
                        setIsContentShared(false)
                        setSharerUserId(null)
                        setCurrentSharedStep(null)

                        // Broadcast to all users that content sharing has stopped
                        // This ensures everyone's UI is updated even if they don't detect the disconnection
                        try {
                            commandChannel?.send(
                                JSON.stringify({
                                    type: 'content_sharing',
                                    action: 'stop',
                                    reason: 'sharer_disconnected',
                                })
                            )
                        } catch (error) {
                            console.error('Error broadcasting sharer disconnection:', error)
                        }
                    }
                }, 2000) // Wait 2 seconds to verify disconnection
            }
        }

        zoomClient.on('user-removed', handleUserRemoved)

        return () => {
            zoomClient.off('user-removed', handleUserRemoved)
        }
    }, [zoomClient, sharerUserId, showToast, commandChannel, getShareUserName, t, enableSharedContent])

    // Periodically verify that the content sharer is still connected
    useEffect(() => {
        // Only run this check if content is being shared and feature is enabled
        if (!isContentShared || !sharerUserId || !enableSharedContent || room === 'general') return

        const checkSharerConnection = () => {
            // Get all current users
            const allUsers = zoomClient.getAllUser()

            // Check if the sharer is still in the meeting - use strict equality with Number conversion
            const sharerStillConnected = allUsers.some(user => Number(user.userId) === Number(sharerUserId))

            if (!sharerStillConnected) {
                // Wait a moment and double-check before taking action (to avoid race conditions)
                setTimeout(() => {
                    // Get fresh list of users
                    const updatedUsers = zoomClient.getAllUser()
                    const sharerStillConnectedAfterDelay = updatedUsers.some(
                        user => Number(user.userId) === Number(sharerUserId)
                    )

                    // Only proceed if the user is still missing after the delay
                    if (!sharerStillConnectedAfterDelay) {
                        // Show a notification
                        const sharerName = getShareUserName(sharerUserId)
                        const icon = <FontAwesomeIcon icon={['far', 'folder-open']} style={{ color: '#48A0F8' }} />

                        // First dismiss any existing notification
                        showToast.dismiss(`content-sharing-${sharerUserId}`)

                        // Then show a temporary notification about disconnection
                        showToast(t('commandChannel.sharerDisconnected', { name: sharerName }), {
                            id: `content-sharing-disconnect-${sharerUserId}`,
                            duration: 5000,
                            icon,
                        })

                        // Reset state
                        setIsContentShared(false)
                        setSharerUserId(null)
                        setCurrentSharedStep(null)

                        // Broadcast to all users
                        try {
                            commandChannel?.send(
                                JSON.stringify({
                                    type: 'content_sharing',
                                    action: 'stop',
                                    reason: 'sharer_disconnected',
                                })
                            )
                        } catch (error) {
                            console.error('Error broadcasting sharer disconnection:', error)
                        }
                    }
                }, 5000) // Wait 5 seconds before confirming disconnection
            }
        }

        // Check every 60 seconds instead of 30 to reduce false positives
        const intervalId = setInterval(checkSharerConnection, 60000)

        return () => {
            clearInterval(intervalId)
        }
    }, [isContentShared, sharerUserId, zoomClient, showToast, commandChannel, getShareUserName, t, enableSharedContent])

    // Handle new users joining
    useEffect(() => {
        if (!enableSharedContent || room === 'general') return

        const handleUserAdded = (newUsers: Participant[]) => {
            // If I'm sharing content and someone new joins, send them the current state
            if (isContentSharer && currentSharedStep !== null) {
                setTimeout(() => {
                    newUsers.forEach(user => {
                        commandChannel?.send(
                            JSON.stringify({
                                type: 'content_sharing',
                                action: 'start',
                                stepId: currentSharedStep,
                            }),
                            user.userId
                        )
                    })
                }, 3000) // Give them time to initialize
            }
        }

        zoomClient.on('user-added', handleUserAdded)

        return () => {
            zoomClient.off('user-added', handleUserAdded)
        }
    }, [zoomClient, commandChannel, isContentSharer, currentSharedStep, enableSharedContent])

    const shareContent = async () => {
        if (!zoomClient || isContentShared || !enableSharedContent || room === 'general') return

        const commandClient = commandChannel
        if (!commandClient) return

        // Get the last seen step from sessionStorage
        let initialStepId = getLastSeenStep()

        // Verify that the step exists in the current session
        const stepExists = initialStepId ? !!findStepById(initialStepId) : false

        // If no step is found in sessionStorage or the step doesn't exist in the current session,
        // use the first step from the study plan
        if (!initialStepId || !stepExists) {
            if (firstStepId) {
                initialStepId = firstStepId
            } else {
                // Try to use the first step from the raw API response if available
                const rawFirstStepId =
                    currentCourse?.courseId && currentCourse.courseId === courseId
                        ? '547fsnVHIcG5I8o6mQgBM5' // Hardcoded fallback based on provided API response
                        : undefined

                if (rawFirstStepId) {
                    initialStepId = rawFirstStepId
                } else {
                    return // Don't proceed if we can't determine a valid step
                }
            }
        }

        try {
            // First update local state
            setIsContentShared(true)
            setSharerUserId(userId)
            setCurrentSharedStep(initialStepId)

            // Save the step to sessionStorage
            saveLastSeenStep(initialStepId || '')

            // Show notification for the sharer
            showToast(
                <NotificationWithCross id={`content-sharing-${userId}`}>
                    <FontAwesomeIcon icon={['far', 'folder-open']} style={{ color: '#48A0F8', marginRight: '8px' }} />
                    {t('commandChannel.youAreSharing')}
                </NotificationWithCross>,
                {
                    id: `content-sharing-${userId}`,
                    duration: Infinity,
                }
            )

            // Then broadcast to others
            await commandClient.send(
                JSON.stringify({
                    type: 'content_sharing',
                    action: 'start',
                    stepId: initialStepId,
                })
            )
        } catch (error) {
            console.error('Error sharing content:', error)
            // Revert state if broadcasting fails
            setIsContentShared(false)
            setSharerUserId(null)
            setCurrentSharedStep(null)
            showToast.dismiss(`content-sharing-${userId}`)
        }
    }

    const stopSharingContent = async () => {
        if (!isContentShared || !isContentSharer || !enableSharedContent || room === 'general') return

        try {
            // Send command to notify others that content sharing has stopped
            await commandChannel?.send(
                JSON.stringify({
                    type: 'content_sharing',
                    action: 'stop',
                })
            )

            // Reset local state
            setIsContentShared(false)
            setSharerUserId(null)
            setCurrentSharedStep(null)

            // Show notification
            showContentSharingNotification('stop', null)
        } catch (error) {
            console.error('Error stopping content sharing:', error)
            // Show error toast
            showToast.error(t('contentSharing.stopError', 'Error stopping content sharing'))
        }
    }

    /**
     * Navigate to a specific step by its unique ID
     *
     * This function sends a command to all participants to navigate to the specified step.
     * We use the actual step ID (e.g., "6BBBww1SmhOjiELwZOjcGs") instead of a numeric index
     * to ensure consistent navigation across different content days and sessions.
     *
     * @param stepId The unique ID of the step to navigate to
     */
    const navigateToStep = async (stepId: string) => {
        if (!zoomClient || !isContentSharer || !enableSharedContent || room === 'general') return

        const commandClient = commandChannel

        // Verify that the step exists in the current session
        const stepExists = !!findStepById(stepId)

        // If the step doesn't exist, use the first step instead
        const finalStepId = stepExists ? stepId : firstStepId

        // If we still don't have a valid step ID, don't proceed
        if (!finalStepId) {
            console.error('Cannot navigate: No valid step ID available')
            return
        }

        try {
            await commandClient?.send(
                JSON.stringify({
                    type: 'content_sharing',
                    action: 'navigate',
                    stepId: finalStepId,
                })
            )

            setCurrentSharedStep(finalStepId)

            // Save the step to sessionStorage for all participants
            saveLastSeenStep(finalStepId)
        } catch (error) {
            console.error('Error navigating to step:', error)
        }
    }

    // Clean up notifications when component unmounts
    useEffect(() => {
        return () => {
            if (sharerUserId) {
                showToast.dismiss(`content-sharing-${sharerUserId}`)
            }
        }
    }, [sharerUserId, showToast])

    // Clean up any active countdown timers when unmounting
    useEffect(() => {
        return () => {
            // Clear all countdown timers
            Object.values(countdownTimers).forEach(timer => clearInterval(timer))
        }
    }, [countdownTimers])

    // Function to convert YouTube URL to embedded format
    const getEmbeddedYouTubeUrl = (url: string): string => {
        // Handle youtu.be format
        if (url.includes('youtu.be')) {
            const videoId = url.split('/').pop()
            return `https://www.youtube.com/embed/${videoId}`
        }

        // Handle youtube.com/watch format
        if (url.includes('youtube.com/watch')) {
            const videoId = new URL(url).searchParams.get('v')
            return `https://www.youtube.com/embed/${videoId}`
        }

        // If already an embed URL or not recognized, return as is
        return url
    }

    // New method to share a video with other participants
    const shareVideo = async (videoUrl: string) => {
        if (!zoomClient || !commandChannel || !enableSharedContent) return

        try {
            // First open the video for myself immediately
            const embeddedUrl = getEmbeddedYouTubeUrl(videoUrl)
            openPip({
                src: embeddedUrl,
                mute: false,
                onClose: () => {
                    // Any cleanup needed when Pip is closed
                },
            })

            // Only broadcast to others if I am the content sharer
            if (isContentSharer) {
                await commandChannel.send(
                    JSON.stringify({
                        type: 'content_sharing',
                        action: 'share_video',
                        videoUrl: videoUrl,
                    })
                )
            }
        } catch (error) {
            console.error('Error sharing video:', error)
        }
    }

    // New method to share an image with other participants
    const shareImage = async (src: string, alt?: string) => {
        if (!zoomClient || !commandChannel || !enableSharedContent) return

        try {
            // Only broadcast if I am the content sharer
            if (isContentSharer) {
                await commandChannel.send(
                    JSON.stringify({
                        type: 'content_sharing',
                        action: 'share_image',
                        src,
                        alt,
                    })
                )
            }
        } catch (error) {
            console.error('Error sharing image:', error)
        }
    }

    return (
        <ContentSharingContext.Provider
            value={{
                isContentShared,
                setIsContentShared,
                isContentSharer,
                currentSharedStep,
                setCurrentSharedStep,
                shareContent,
                stopSharingContent,
                navigateToStep,
                shareVideo,
                shareImage,
                isFeatureEnabled: enableSharedContent,
                currentStepId,
                setCurrentStepId,
            }}
        >
            {children}
        </ContentSharingContext.Provider>
    )
}

export const useContentSharing = () => {
    const context = useContext(ContentSharingContext)
    if (context === undefined) {
        throw new Error('useContentSharing must be used within a ContentSharingProvider')
    }
    return context
}
